import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getHome } from "./utils/getHome";
import styles from "./homePage.module.scss";
import assets from "./assets";
import FormSection from "./components/FormSection";
import WhatWeDo from "./components/WhatWeDo";
import ProjectsSection from "./components/ProjectsSection";
import HeroSection from "./components/HeroSection";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import LoadingScreen from "./components/LoadingScreen";
import Arrow from "./components/Arrow";
import Video from "./components/Video";
import { useRef } from "react";



export default function Home() {
  const [loading, setLoading] = useState(false);
  const [home, setHome] = useState(null);
  const [data, setData] = useState(null);
  const { i18n, t } = useTranslation();
  const { locale } = useParams();
  const location = useLocation();
  const ref = useRef();
  const aboutRef = useRef();
  const navigate = useNavigate();

  const formContent = {
    title: t("home.contactusTitle"),
    formTitle: t("home.contactUsDescTitle"),
    formDescription: t("home.contactUsDescBody"),
  };

  const fields = [
    {
      fieldName: "first_name",
      label: t("labels.firstname"),
      type: "text",
      validations: {
        required: t("validations.required"),
        minLength: { value: 2, message: t("validations.shortLength") },
        maxLength: { value: 30, message: t("validations.tooLong") },
      },
    },
    {
      fieldName: "last_name",
      label: t("labels.lastname"),
      type: "text",
      validations: {
        required: t("validations.required"),
        minLength: { value: 2, message: t("validations.shortLength") },
        maxLength: { value: 30, message: t("validations.tooLong") },
      },
    },
    {
      fieldName: "email",
      label: t("labels.email"),
      type: "email",
      validations: {
        required: "This Field is required",
        pattern: {
          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          message: "Invalid Email",
        },
        maxLength: { value: 100, message: t("validations.tooLong") },
      },
    },
    {
      fieldName: "phone",
      label: t("labels.phonenumber"),
      type: "number",
      validations: {
        required: t("validations.required"),
        pattern: {
          value: /^(01)[0-9]{9}$/,
          message: t("validations.invalidPhonenumber"),
        },
        maxLength: { value: 11, message: t("validations.tooLong") },
      },
    },
    {
      fieldName: "message",
      label: t("labels.message"),
      type: "textarea",
      fullWidth: true,
      validations: {
        maxLength: {
          value: 1000,
          message: t("validations.tooLong"),
        },
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    if (locale) i18n.changeLanguage(locale);
    async function startFetch() {
      setHome(null);
      const res = await getHome(locale);
      setHome(res);
      setData(res.company_info[0]);
      setLoading(false);
    }
    startFetch();
  }, [locale]);

  useEffect(() => {
    if (location.hash === "#contact") {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }
    if (location.hash === "#about") {
      aboutRef.current?.scrollIntoView({ behavior: "smooth" });
      navigate(`/${locale}/`);
    }
  }, [location, ref?.current]);

  if (loading) {
    return <LoadingScreen />;
  }
  const socialMedia = [
    {
      type: "facebookIcon",
      link: home?.company_info?.[0].facebook,
    },
    {
      type: "linkedInIcon",
      link: home?.company_info?.[0].linkedin,
    },
    {
      type: "instgramIcon",
      link: home?.company_info?.[0].instagram,
    },
  ];
  return (
    <>
      <Navbar light={true} />
      <main className={styles.main}>
        <HeroSection aboutRef={aboutRef} slides={home?.main_section}>
          <div className={styles.heroContent}>
            <h2 className={styles.heroContentTitle}>{t("home.heroTitle")}</h2>
            <div className={styles.heroContentRight}>
              <p className={styles.heroContentBody}>{t("home.heroBody")}</p>
              <a className={styles.heroContentButton} href={`/${locale}/about`}>
                {t("home.heroButton")}
                <Arrow style={{ rotate: "90deg" }} color={"#fff"} />
              </a>
            </div>
          </div>
        </HeroSection>
        <div ref={aboutRef} id="#about"></div>
        <WhatWeDo content={home?.what_we_do} body={home?.what_we_do_collection_text} />
        <Video
          poster={home?.about_us.thumbnail_video}
          src={home?.about_us.video}
          autoplay={false}
        />
        <ProjectsSection projects={home?.projects} />
        <div ref={ref} id="contact"></div>
        <FormSection content={formContent} formFields={fields}>
          <div className={styles.formInfo}>
            <div className={styles.formInfoEmail}>
              {t("form.mail")}
              <p className={styles.formInfoBody}>
                {home?.company_info?.[0].email}
              </p>
            </div>
            <div className={styles.formInfoPhone}>
              {t("form.phone")}
              <p className={styles.formInfoBody}>
                {home?.company_info?.[0].phone}
              </p>
            </div>
            <div className={styles.formInfoAddress}>
              {t("form.address")}
              <p className={styles.formInfoBody}>
                {home?.company_info?.[0].address}
              </p>
            </div>
            <div className={styles.formInfoSocial}>
              {t("form.social_media")}
              <div>
                {socialMedia.map((social, index) => {
                  return (
                    <a key={index} href={social.link} target="_blank">
                      <img src={assets.icons[social.type]} alt={social.type} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </FormSection>
      </main>
      <Footer data={data} />
    </>
  );
}
